import { useEffect, useMemo, useRef } from "react";
import { ChevronUp, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageItem } from "./MessageItem";
import type { ChatMessage } from "@/lib/concord/types";

const GROUP_WINDOW_MS = 5 * 60 * 1000;

interface MessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadOlder: () => void;
  myPub: string;
  onReact: (message: ChatMessage, emoji: string) => void;
  onDelete: (message: ChatMessage) => void;
  channelName?: string;
}

function DayDivider({ ms }: { ms: number }) {
  const d = new Date(ms);
  const today = new Date().toDateString() === d.toDateString();
  const label = today
    ? "Today"
    : d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="my-4 flex items-center gap-3 px-4" role="separator">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function MessageList({
  messages,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadOlder,
  myPub,
  onReact,
  onDelete,
  channelName,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastCount = useRef(0);

  const rows = useMemo(() => {
    return messages.map((m, i) => {
      const prev = messages[i - 1];
      const grouped =
        prev !== undefined &&
        prev.pubkey === m.pubkey &&
        m.createdAtMs - prev.createdAtMs < GROUP_WINDOW_MS &&
        new Date(prev.createdAtMs).toDateString() === new Date(m.createdAtMs).toDateString();
      const newDay =
        prev === undefined ||
        new Date(prev.createdAtMs).toDateString() !== new Date(m.createdAtMs).toDateString();
      return { m, grouped, newDay };
    });
  }, [messages]);

  // Track whether the reader is near the bottom; only autoscroll then.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      stickToBottom.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const grew = messages.length > lastCount.current;
    lastCount.current = messages.length;
    if (grew && stickToBottom.current) {
      bottomRef.current?.scrollIntoView({ block: "end" });
    }
  }, [messages.length]);

  // First load: land at the bottom.
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      bottomRef.current?.scrollIntoView({ block: "end" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="flex-1 space-y-6 p-4 overflow-hidden">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="py-4">
        <div className="flex justify-center">
          {hasNextPage ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onLoadOlder}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ChevronUp className="size-4" />
              )}
              Load earlier messages
            </Button>
          ) : (
            messages.length > 0 && (
              <p className="px-4 pb-2 text-center text-xs text-muted-foreground">
                The beginning of {channelName ? `#${channelName}` : "this channel"} — the
                chama&rsquo;s living history.
              </p>
            )
          )}
        </div>

        {messages.length === 0 ? (
          <div className="mx-4 rounded-xl border border-dashed p-12 text-center">
            <p className="text-muted-foreground max-w-sm mx-auto">
              No messages yet. Karibu — say the first hello.
            </p>
          </div>
        ) : (
          rows.map(({ m, grouped, newDay }) => (
            <div key={m.id}>
              {newDay && <DayDivider ms={m.createdAtMs} />}
              <MessageItem
                message={m}
                grouped={grouped && !newDay}
                isMine={m.pubkey === myPub}
                onReact={(emoji) => onReact(m, emoji)}
                onDelete={() => onDelete(m)}
              />
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
